/**
 * Tipi e helper fetch del modulo email (pattern fetch nativo + Bearer,
 * come gli altri moduli; nessun wrapper condiviso nella shell).
 */

export interface EmailAccount {
  id: string;
  tipo: string;
  indirizzo: string;
  nome_visualizzato: string | null;
  firma: string | null;
  imap_host: string | null;
  imap_port: number | null;
  smtp_host: string | null;
  smtp_port: number | null;
  imap_ssl: boolean;
  smtp_ssl: boolean;
  sent_folder: string | null;
  trash_folder: string | null;
  drafts_folder: string | null;
  stato: string;
  sync_abilitato: boolean;
  ultimo_sync_at: string | null;
  ultimo_errore: string | null;
  non_letti: number;
}

export interface EmailCartella {
  id: string;
  nome_imap: string;
  nome_display: string | null;
  ruolo: string | null;
  num_totali: number;
  num_non_letti: number;
}

/**
 * Stato dell'attesa di risposta su una mail inviata.
 * `scaduto` non esiste a DB: lo calcola il backend confrontando la scadenza con adesso.
 */
export type StatoMonitoraggio = 'in_attesa' | 'scaduto' | 'risposto' | 'chiuso';

export interface MonitoraggioRef {
  id: string;
  stato: StatoMonitoraggio;
  scadenza: string;
}

export interface EmailMessaggioItem {
  id: string;
  uid: number;
  da_nome: string | null;
  da_email: string | null;
  oggetto: string | null;
  anteprima: string | null;
  data_ricezione: string | null;
  flag_letto: boolean;
  flag_flagged: boolean;
  flag_risposto: boolean;
  ha_allegati: boolean;
  etichette: string[];
  monitoraggio?: MonitoraggioRef | null;
}

/** Etichetta email condivisa (CRUD /email/etichette). colore = "#rrggbb". */
export interface EmailEtichetta {
  id: string;
  nome: string;
  colore: string;
  ordine: number;
  attiva: boolean;
}

export interface EmailAllegato {
  id: string;
  filename: string | null;
  content_type: string | null;
  dimensione: number | null;
  inline: boolean;
  phishing_verdict: Record<string, unknown> | null;
}

export interface EmailMessaggioDetail extends EmailMessaggioItem {
  a_json: { nome: string | null; email: string }[] | null;
  cc_json: { nome: string | null; email: string }[] | null;
  reply_to: string | null;
  message_id: string | null;
  corpo_testo: string | null;
  corpo_html: string | null;
  allegati: EmailAllegato[];
}

export interface MessaggiPage {
  items: EmailMessaggioItem[];
  totale: number;
  page: number;
  page_size: number;
}

// --- Regole automatiche (Fase 3) ---

export interface RegolaCondizione {
  campo: string;
  operatore: string;
  valore: string | boolean;
}

export interface RegolaAzione {
  tipo: string;
  cartella_id?: string | null;
  etichetta_id?: string | null;
}

export interface EmailRegola {
  id: string;
  account_id: string | null;
  nome: string;
  priorita: number;
  attiva: boolean;
  match_tipo: 'all' | 'any';
  condizioni: RegolaCondizione[];
  azioni: RegolaAzione[];
  stop_su_match: boolean;
}

export interface DryRunMatch {
  id: string;
  da_email: string | null;
  da_nome: string | null;
  oggetto: string | null;
  data_ricezione: string | null;
}

export interface DryRunEsito {
  totale_esaminati: number;
  match: DryRunMatch[];
}

export interface BackfillEsito {
  esaminati: number;
  match: number;
  azioni_ok: number;
  azioni_err: number;
}

// --- Azioni batch (multi-selezione) ---

export type BatchAzione =
  | 'sposta'
  | 'letto'
  | 'non_letto'
  | 'archivia'
  | 'elimina'
  | 'applica_etichetta'
  | 'rimuovi_etichetta';

export interface BatchEsito {
  ok: number;
  errori: { id: string; motivo: string }[];
}

/** Label esito per l'avviso in testata. */
export const BATCH_AZIONE_LABEL: Record<BatchAzione, string> = {
  sposta: 'spostati',
  letto: 'segnati letti',
  non_letto: 'segnati da leggere',
  archivia: 'archiviati',
  elimina: 'eliminati',
  applica_etichetta: 'etichettati',
  rimuovi_etichetta: 'senza etichetta',
};

/** Label italiane dei campi condizione (mappa unica: la UI legge SOLO da qui). */
export const REGOLA_CAMPI: { value: string; label: string }[] = [
  { value: 'mittente', label: 'Mittente' },
  { value: 'destinatari', label: 'Destinatari (A/CC)' },
  { value: 'oggetto', label: 'Oggetto' },
  { value: 'corpo', label: 'Corpo' },
  { value: 'ha_allegati', label: 'Ha allegati' },
];

/** Label italiane degli operatori sui campi testo. */
export const REGOLA_OPERATORI: { value: string; label: string }[] = [
  { value: 'contiene', label: 'contiene' },
  { value: 'non_contiene', label: 'non contiene' },
  { value: 'uguale', label: 'uguale a' },
  { value: 'inizia_con', label: 'inizia con' },
  { value: 'finisce_con', label: 'finisce con' },
];

/** Label italiane delle azioni. sposta/archivia/elimina sono di spostamento (max una per regola). */
export const REGOLA_AZIONI: { value: string; label: string; spostamento: boolean }[] = [
  { value: 'sposta', label: 'Sposta in cartella', spostamento: true },
  { value: 'segna_letto', label: 'Segna come letto', spostamento: false },
  { value: 'flag', label: 'Aggiungi flag', spostamento: false },
  { value: 'archivia', label: 'Archivia', spostamento: true },
  { value: 'elimina', label: 'Elimina (Cestino)', spostamento: true },
  { value: 'applica_etichetta', label: 'Applica etichetta', spostamento: false },
];

// --- Etichette (tag) su messaggio / registro ---

/**
 * Registro etichette del tenant, ordinate per ordine/nome.
 *
 * Di default solo le attive (per picker/sidebar/filtri). includeDisattivate
 * chiede anche quelle disattivate: il backend richiede in tal caso il
 * permesso email.etichette_gestione (403 altrimenti) - uso riservato al
 * pannello di gestione (EtichetteManager).
 */
export async function listaEtichette(includeDisattivate = false): Promise<EmailEtichetta[]> {
  const res = await emailFetch(
    includeDisattivate ? '/etichette?include_disattivate=true' : '/etichette'
  );
  return res.ok ? res.json() : [];
}

/** Crea una nuova etichetta (o riattiva quella disattivata con lo stesso nome). */
export async function creaEtichetta(dati: {
  nome: string;
  colore: string;
  ordine?: number;
}): Promise<Response> {
  return emailFetch('/etichette', { method: 'POST', body: JSON.stringify(dati) });
}

/** Aggiorna nome/colore/ordine e/o riattiva esplicitamente (attiva: true) un'etichetta. */
export async function aggiornaEtichetta(
  id: string,
  dati: { nome?: string; colore?: string; ordine?: number; attiva?: boolean }
): Promise<Response> {
  return emailFetch(`/etichette/${id}`, { method: 'PATCH', body: JSON.stringify(dati) });
}

/** Disattiva (soft-delete) un'etichetta. */
export async function disattivaEtichetta(id: string): Promise<Response> {
  return emailFetch(`/etichette/${id}`, { method: 'DELETE' });
}

/**
 * Avvia l'attesa di risposta su una mail inviata.
 * Idempotente lato server: due click non creano due monitoraggi.
 */
export function avviaMonitoraggio(messaggioId: string, giorni: number): Promise<Response> {
  return emailFetch('/monitoraggi', {
    method: 'POST',
    body: JSON.stringify({ messaggio_id: messaggioId, giorni }),
  });
}

/** Smette di attendere una risposta. Non riapre nulla se gia' chiuso. */
export function chiudiMonitoraggio(monitoraggioId: string): Promise<Response> {
  return emailFetch(`/monitoraggi/${monitoraggioId}/chiudi`, { method: 'POST' });
}

/** Applica un'etichetta a un messaggio. Risposta = lista aggiornata degli id etichetta. */
export function applicaEtichettaMessaggio(
  messaggioId: string,
  etichettaId: string
): Promise<Response> {
  return emailFetch(`/messaggi/${messaggioId}/etichette`, {
    method: 'POST',
    body: JSON.stringify({ etichetta_id: etichettaId }),
  });
}

/** Rimuove un'etichetta da un messaggio. Risposta = lista aggiornata degli id etichetta. */
export function rimuoviEtichettaMessaggio(
  messaggioId: string,
  etichettaId: string
): Promise<Response> {
  return emailFetch(`/messaggi/${messaggioId}/etichette/${etichettaId}`, {
    method: 'DELETE',
  });
}

/** Esegue una fetch autenticata verso l'API del modulo email. */
export async function emailFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('token');
  return fetch(`/api/v1/email${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
}
